import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Building2, Users, Shield, Cpu, ArrowLeft, ArrowRight, Save, 
  CheckCircle, Star, Loader2, FileText, AlertCircle, HelpCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import API_URL from '../lib/apiUrl';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import {
  PRO_CATEGORIES,
  PRO_QUESTION_TYPES,
  calculateCategoryScore,
  calculateProOverallScore
} from '../data/proAssessmentQuestions';


// Star Rating Component
const StarRating = ({ value, onChange, disabled }) => {
  const [hovered, setHovered] = useState(0);
  
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
      <div className="flex items-center gap-0.5 sm:gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            disabled={disabled}
            className={`p-0.5 sm:p-1 transition-all ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:scale-110'}`}
            onMouseEnter={() => !disabled && setHovered(star)}
            onMouseLeave={() => !disabled && setHovered(0)}
            onClick={() => !disabled && onChange(star)}
          >
            <Star
              className={`h-6 w-6 sm:h-8 sm:w-8 transition-colors ${
                (hovered || value) >= star
                  ? 'fill-yellow-400 text-yellow-400'
                  : 'text-slate-300'
              }`}
            />
          </button>
        ))}
      </div>
      <span className="text-xs sm:text-sm text-slate-500">
        {value ? `${value}/5 - ${getRatingLabel(value)}` : 'Select rating'}
      </span>
    </div>
  );
};

const getRatingLabel = (value) => {
  const labels = {
    1: 'Initial',
    2: 'Developing',
    3: 'Defined',
    4: 'Managed',
    5: 'Optimizing'
  };
  return labels[value] || '';
};

// Yes/No/Unaware Button Group Component
const YesNoUnaware = ({ value, onChange, disabled }) => {
  const options = [
    { value: 'yes', label: 'Yes', color: 'bg-green-100 text-green-700 border-green-300 hover:bg-green-200', activeColor: 'bg-green-500 text-white border-green-500' },
    { value: 'no', label: 'No', color: 'bg-red-100 text-red-700 border-red-300 hover:bg-red-200', activeColor: 'bg-red-500 text-white border-red-500' },
    { value: 'unaware', label: 'Unaware', color: 'bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-200', activeColor: 'bg-amber-500 text-white border-amber-500' }
  ];
  
  return (
    <div className="flex flex-wrap gap-2 sm:gap-3">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          disabled={disabled}
          onClick={() => !disabled && onChange(option.value)}
          className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg border-2 font-medium text-sm transition-all ${
            disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
          } ${value === option.value ? option.activeColor : option.color}`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
};

// True/False Button Group Component
const TrueFalse = ({ value, onChange, disabled }) => {
  const options = [
    { value: 'true', label: 'True', color: 'bg-blue-100 text-blue-700 border-blue-300 hover:bg-blue-200', activeColor: 'bg-blue-500 text-white border-blue-500' },
    { value: 'false', label: 'False', color: 'bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-200', activeColor: 'bg-slate-600 text-white border-slate-600' }
  ];
  
  return (
    <div className="flex gap-2 sm:gap-3">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          disabled={disabled}
          onClick={() => !disabled && onChange(option.value)}
          className={`px-6 sm:px-8 py-2 sm:py-2.5 rounded-lg border-2 font-medium text-sm transition-all ${
            disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
          } ${value === option.value ? option.activeColor : option.color}`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
};

// Question Card Component
const QuestionCard = ({ control, index, value, onChange, disabled }) => {
  const renderInput = () => {
    switch (control.type) {
      case PRO_QUESTION_TYPES.RATING:
        return <StarRating value={value} onChange={onChange} disabled={disabled} />;
      case PRO_QUESTION_TYPES.YES_NO:
        return <YesNoUnaware value={value} onChange={onChange} disabled={disabled} />;
      case PRO_QUESTION_TYPES.TRUE_FALSE:
        return <TrueFalse value={value} onChange={onChange} disabled={disabled} />;
      default:
        return null;
    }
  };
  
  const getTypeLabel = () => {
    switch (control.type) {
      case PRO_QUESTION_TYPES.RATING: return '⭐ Rating';
      case PRO_QUESTION_TYPES.YES_NO: return '✅ Yes/No';
      case PRO_QUESTION_TYPES.TRUE_FALSE: return '☑️ T/F';
      default: return '';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03 }}
      className={`bg-white rounded-xl border-2 p-4 md:p-6 transition-all ${
        value ? 'border-green-200 bg-green-50/30' : 'border-slate-200 hover:border-slate-300'
      }`}
    >
      <div className="flex items-start justify-between mb-3 md:mb-4">
        <div className="flex items-start gap-2 md:gap-3">
          <span className="flex items-center justify-center min-w-[40px] h-8 md:w-10 md:h-10 rounded-lg bg-slate-100 text-slate-600 font-mono text-xs md:text-sm font-bold">
            {control.controlId}
          </span>
          <div>
            <h4 className="font-semibold text-sm md:text-base text-slate-900 leading-tight">{control.controlName}</h4>
            <span className="text-[10px] md:text-xs text-slate-500">{getTypeLabel()}</span>
          </div>
        </div>
        {value && (
          <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-green-500 flex-shrink-0" />
        )}
      </div>
      
      <p className="text-sm md:text-base text-slate-700 mb-3 md:mb-4 leading-relaxed">{control.question}</p>
      
      <div className="mb-3 md:mb-4">
        {renderInput()}
      </div>
      
      <div className="flex items-center gap-2 text-[10px] md:text-xs text-slate-400 border-t border-slate-100 pt-2 md:pt-3 mt-2 md:mt-3">
        <FileText className="h-3 w-3 flex-shrink-0" />
        <span className="truncate">Ref: {control.policyDocument}</span>
      </div>
    </motion.div>
  );
};

// Category Tab Component
const CategoryTab = ({ category, isActive, onClick, progress }) => {
  const icons = {
    organizational: Building2,
    people: Users,
    physical: Shield,
    technological: Cpu
  };
  const Icon = icons[category.id] || Building2;
  
  const colors = {
    organizational: 'blue',
    people: 'purple',
    physical: 'green',
    technological: 'orange'
  };
  const color = colors[category.id] || 'blue';
  
  return (
    <button
      onClick={onClick}
      className={`min-w-[140px] md:min-w-0 flex-1 px-3 md:px-4 py-3 md:py-4 rounded-xl transition-all ${
        isActive 
          ? `bg-${color}-50 border-2 border-${color}-200 shadow-sm` 
          : 'bg-white border-2 border-slate-200 hover:border-slate-300'
      }`}
    >
      <div className="flex items-center gap-2 md:gap-3">
        <div className={`p-1.5 md:p-2 rounded-lg ${isActive ? `bg-${color}-100` : 'bg-slate-100'}`}>
          <Icon className={`h-4 w-4 md:h-5 md:w-5 ${isActive ? `text-${color}-600` : 'text-slate-500'}`} />
        </div>
        <div className="text-left">
          <p className={`font-semibold text-xs md:text-sm ${isActive ? 'text-slate-900' : 'text-slate-600'}`}>
            {category.shortName}
          </p>
          <p className="text-[10px] md:text-xs text-slate-500">{category.count} controls</p>
        </div>
      </div>
      <div className="mt-2 md:mt-3">
        <div className="flex items-center justify-between text-[10px] md:text-xs mb-1">
          <span className="text-slate-500">Progress</span>
          <span className={`font-medium ${progress === 100 ? 'text-green-600' : 'text-slate-600'}`}>
            {progress}%
          </span>
        </div>
        <div className="h-1 md:h-1.5 bg-slate-200 rounded-full overflow-hidden">
          <div 
            className={`h-full transition-all duration-500 ${
              progress === 100 ? 'bg-green-500' : `bg-${color}-500`
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </button>
  );
};

// Main Pro Assessment Component
const ProAssessment = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  
  const [activeCategory, setActiveCategory] = useState('organizational');
  const [answers, setAnswers] = useState({
    organizational: {},
    people: {},
    physical: {},
    technological: {}
  });
  const [saving, setSaving] = useState(false);
  const assessmentLoadedRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [orgName, setOrgName] = useState('');

  // Check if user has Pro plan
  const isPro = user?.subscription?.plan === 'pro';

  // Load existing assessment data
  const loadAssessment = useCallback(async () => {
    setLoading(true);
    try {
      const [orgRes, assessRes] = await Promise.all([
        axios.get(`${API_URL}/api/organizations/${orgId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/pro-assessment/${orgId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null }))
      ]);
      
      setOrgName(orgRes.data?.org_name || 'Organization');
      
      if (assessRes.data?.answers) {
        setAnswers(assessRes.data.answers);
      }
      if (assessRes.data?.activeCategory && PRO_CATEGORIES[assessRes.data.activeCategory]) {
        setActiveCategory(assessRes.data.activeCategory);
      }
      assessmentLoadedRef.current = true;
    } catch (error) {
      console.error('Error loading assessment:', error);
      toast.error('Failed to load assessment data');
    } finally {
      setLoading(false);
    }
  }, [orgId, token]);

  useEffect(() => {
    loadAssessment();
  }, [loadAssessment]);

  // Handle answer change
  const handleAnswerChange = (controlId, value) => {
    setAnswers(prev => ({
      ...prev,
      [activeCategory]: {
        ...prev[activeCategory],
        [controlId]: value
      }
    }));
  };

  // Save assessment
  const saveAssessment = async (showToast = true) => {
    setSaving(true);
    try {
      const scores = calculateProOverallScore(answers);
      
      await axios.post(`${API_URL}/api/pro-assessment/${orgId}`, {
        answers,
        scores,
        activeCategory,
        lastUpdated: new Date().toISOString()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (showToast) {
        toast.success('Assessment saved successfully');
      }
    } catch (error) {
      console.error('Error saving assessment:', error);
      toast.error('Failed to save assessment');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!assessmentLoadedRef.current) return undefined;
    const hasAnswers = Object.values(answers).some((category) => Object.keys(category || {}).length > 0);
    if (!hasAnswers) return undefined;
    const timer = setTimeout(() => saveAssessment(false), 1500);
    return () => clearTimeout(timer);
    // saveAssessment intentionally reads the latest answers and category from this render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, activeCategory]);

  useEffect(() => {
    const saveOnExit = () => {
      if (!assessmentLoadedRef.current) return;
      const hasAnswers = Object.values(answers).some((category) => Object.keys(category || {}).length > 0);
      if (!hasAnswers) return;
      fetch(`${API_URL}/api/pro-assessment/${orgId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ answers, activeCategory, lastUpdated: new Date().toISOString() }),
        keepalive: true
      }).catch(() => undefined);
    };
    window.addEventListener('pagehide', saveOnExit);
    return () => window.removeEventListener('pagehide', saveOnExit);
  }, [answers, activeCategory, orgId, token]);

  // Calculate progress for each category
  const getCategoryProgress = (categoryId) => {
    const category = PRO_CATEGORIES[categoryId];
    const categoryAnswers = answers[categoryId] || {};
    const answeredCount = Object.keys(categoryAnswers).filter(k => categoryAnswers[k]).length;
    return Math.round((answeredCount / category.count) * 100);
  };

  // Get current category data
  const currentCategory = PRO_CATEGORIES[activeCategory];
  const currentControls = currentCategory?.controls || [];

  // Navigation handlers
  const categoryOrder = ['organizational', 'people', 'physical', 'technological'];
  const currentIndex = categoryOrder.indexOf(activeCategory);
  
  const goToNextCategory = () => {
    if (currentIndex < categoryOrder.length - 1) {
      saveAssessment(false);
      setActiveCategory(categoryOrder[currentIndex + 1]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  
  const goToPrevCategory = () => {
    if (currentIndex > 0) {
      saveAssessment(false);
      setActiveCategory(categoryOrder[currentIndex - 1]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const finishAssessment = async () => {
    await saveAssessment(true);
    navigate(`/assessment-summary/${orgId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading Controls Assessment...</p>
        </div>
      </div>
    );
  }

  if (!isPro) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md text-center shadow-lg border border-slate-200">
          <AlertCircle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Pro Plan Required</h2>
          <p className="text-slate-600 mb-6">
            The Controls Assessment (93 Controls) is available exclusively for Pro Pack users. 
            Upgrade to access the complete ISO 27001:2022 Annex A assessment.
          </p>
          <Button onClick={() => navigate('/dashboard')} className="w-full">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const overallProgress = Math.round(
    (getCategoryProgress('organizational') * 0.4 +
     getCategoryProgress('people') * 0.09 +
     getCategoryProgress('physical') * 0.15 +
     getCategoryProgress('technological') * 0.36)
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 md:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2 md:gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/documentation-readiness/${orgId}`)}
                className="px-2 md:px-3"
              >
                <ArrowLeft className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Back</span>
              </Button>
              <div>
                <h1 className="text-base md:text-xl font-bold text-slate-900">
                  Controls Assessment - ISO 27001:2022
                </h1>
                <p className="text-xs md:text-sm text-slate-500">{orgName} • 93 Annex A Controls</p>
              </div>
            </div>
            
            <div className="flex items-center justify-between sm:justify-end gap-3 md:gap-4">
              <div className="text-left sm:text-right">
                <p className="text-xs text-slate-500">Progress</p>
                <p className="text-base md:text-lg font-bold text-slate-900">{overallProgress}%</p>
              </div>
              <Button onClick={() => saveAssessment(true)} disabled={saving} size="sm">
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Save className="h-4 w-4 md:mr-2" />
                    <span className="hidden md:inline">Save</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 md:px-4 py-4 md:py-8">
        {/* Category Tabs - Horizontal scroll on mobile */}
        <div className="overflow-x-auto pb-2 -mx-3 px-3 md:mx-0 md:px-0 mb-6 md:mb-8">
          <div className="flex md:grid md:grid-cols-4 gap-3 md:gap-4 min-w-max md:min-w-0">
            {Object.values(PRO_CATEGORIES).map((category) => (
              <CategoryTab
                key={category.id}
                category={category}
                isActive={activeCategory === category.id}
                onClick={() => {
                  saveAssessment(false);
                  setActiveCategory(category.id);
                }}
                progress={getCategoryProgress(category.id)}
              />
            ))}
          </div>
        </div>

        {/* Category Header */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4 md:p-6 mb-4 md:mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <h2 className="text-lg md:text-2xl font-bold text-slate-900 mb-1">
                {currentCategory.name}
              </h2>
              <p className="text-sm md:text-base text-slate-600">{currentCategory.description}</p>
              <p className="text-xs md:text-sm text-slate-500 mt-2">
                {currentCategory.controlRange} • {currentCategory.count} Questions
              </p>
            </div>
            <div className="text-left md:text-right flex md:block items-center gap-2">
              <div className="text-2xl md:text-3xl font-bold text-slate-900">
                {getCategoryProgress(activeCategory)}%
              </div>
              <p className="text-xs md:text-sm text-slate-500">Completed</p>
            </div>
          </div>
        </div>

        {/* Questions */}
        <div className="space-y-3 md:space-y-4 mb-6 md:mb-8">
          <AnimatePresence mode="wait">
            {currentControls.map((control, index) => (
              <QuestionCard
                key={control.id}
                control={control}
                index={index}
                value={answers[activeCategory]?.[control.id]}
                onChange={(value) => handleAnswerChange(control.id, value)}
                disabled={saving}
              />
            ))}
          </AnimatePresence>
        </div>

        {/* Navigation */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-white rounded-2xl border border-slate-200 p-4 md:p-6">
          <Button
            variant="outline"
            onClick={goToPrevCategory}
            disabled={currentIndex === 0}
            className="order-2 sm:order-1"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Previous: </span>
            {currentIndex > 0 ? PRO_CATEGORIES[categoryOrder[currentIndex - 1]].shortName : 'Start'}
          </Button>
          
          <div className="flex items-center justify-center gap-2 order-1 sm:order-2">
            {categoryOrder.map((cat, idx) => (
              <div
                key={cat}
                className={`w-2.5 h-2.5 md:w-3 md:h-3 rounded-full transition-all ${
                  idx === currentIndex 
                    ? 'bg-blue-500 w-6 md:w-8' 
                    : getCategoryProgress(cat) === 100 
                      ? 'bg-green-500' 
                      : 'bg-slate-300'
                }`}
              />
            ))}
          </div>
          
          {currentIndex < categoryOrder.length - 1 ? (
            <Button onClick={goToNextCategory} className="order-3">
              <span className="hidden sm:inline">Next: </span>
              {PRO_CATEGORIES[categoryOrder[currentIndex + 1]].shortName}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={finishAssessment} className="bg-green-600 hover:bg-green-700 order-3">
              <CheckCircle className="h-4 w-4 mr-2" />
              Complete & View Summary
            </Button>
          )}
        </div>

        {/* Help Section */}
        <div className="mt-8 bg-blue-50 rounded-xl border border-blue-200 p-6">
          <div className="flex items-start gap-4">
            <HelpCircle className="h-6 w-6 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">Assessment Guide</h3>
              <div className="grid md:grid-cols-3 gap-4 text-sm text-blue-800">
                <div>
                  <p className="font-medium">⭐ 5-Star Rating</p>
                  <p className="text-blue-700">Rate maturity from 1 (Initial) to 5 (Optimizing)</p>
                </div>
                <div>
                  <p className="font-medium">✅ Yes/No/Unaware</p>
                  <p className="text-blue-700">Select Yes if implemented, No if not, Unaware if unknown</p>
                </div>
                <div>
                  <p className="font-medium">☑️ True/False</p>
                  <p className="text-blue-700">Validate the statement as True or False</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProAssessment;
